export const fullAccessRoles = ['superuser', 'superadmin'] as const
export const restrictedAccessRoles = ['manager', 'supervisor', 'admin'] as const

/**
 * Access tiers used by the routing/permission model.
 * - `full`       — superusers/superadmins; the whole product.
 * - `restricted` — managers/supervisors/admins; the dashboard only.
 * - `public`     — any authenticated user regardless of role (e.g. the 403 page).
 */
export type AccessTier = 'full' | 'restricted' | 'public'

/** The tier a user belongs to, derived from their roles. */
export type UserTier = 'full' | 'restricted' | 'none'

export const getUserTier = (
  roles: readonly string[] | undefined | null
): UserTier => {
  const userRoles = roles ?? []
  if (fullAccessRoles.some((role) => userRoles.includes(role))) return 'full'
  if (restrictedAccessRoles.some((role) => userRoles.includes(role)))
    return 'restricted'
  return 'none'
}

/** Whether a user of `tier` may open a route requiring `required`. */
export const tierCanAccess = (
  tier: UserTier,
  required: AccessTier
): boolean => {
  if (required === 'public') return true
  if (required === 'restricted') return tier === 'full' || tier === 'restricted'
  return tier === 'full' // required === 'full'
}

/** The path a user of `tier` is allowed to land on (their "home"). */
export const tierLanding = (tier: UserTier): string => {
  if (tier === 'full') return '/'
  if (tier === 'restricted') return '/dashboard'
  return '/403'
}

/**
 * Whether the company carried in the user's JWT matches the company being viewed.
 * Case-insensitive; false if either value is missing. Centralised so the router and
 * the header agree on the cross-tenant check — and on the lower-casing (the source of
 * a past mismatch bug when one call site forgot to normalise the case).
 */
export const isCompanyTokenValid = (
  companyNameKey: string | undefined | null,
  tokenCompanyNameKey: string | undefined | null
): boolean =>
  !!companyNameKey &&
  !!tokenCompanyNameKey &&
  companyNameKey.toLowerCase() === tokenCompanyNameKey.toLowerCase()

declare module 'vue-router' {
  interface RouteMeta {
    /**
     * Minimum access tier required to open this route. Routes without it are
     * treated as `full` (default-deny): a new route stays locked to superusers
     * until someone deliberately opens it up.
     */
    access?: AccessTier
  }
}
